import * as RadixAccordion from '@radix-ui/react-accordion';
import { ChevronDown } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '../lib/cn';

/**
 * Disclosure. Used by the FAQ, where the accessible semantics matter as much as
 * the animation: each question is a button that expands its answer, and the
 * answer is reachable by keyboard and readable by a screen reader whether or
 * not it is visible.
 */
export const Accordion = RadixAccordion.Root;

export const AccordionItem = forwardRef<
  ElementRef<typeof RadixAccordion.Item>,
  ComponentPropsWithoutRef<typeof RadixAccordion.Item>
>(function AccordionItem({ className, ...props }, ref) {
  return (
    <RadixAccordion.Item
      ref={ref}
      className={cn('border-b border-border last:border-b-0', className)}
      {...props}
    />
  );
});

export const AccordionTrigger = forwardRef<
  ElementRef<typeof RadixAccordion.Trigger>,
  ComponentPropsWithoutRef<typeof RadixAccordion.Trigger>
>(function AccordionTrigger({ className, children, ...props }, ref) {
  return (
    <RadixAccordion.Header className="flex">
      <RadixAccordion.Trigger
        ref={ref}
        className={cn(
          'flex flex-1 items-center justify-between gap-4 py-4 text-start',
          'font-medium text-text transition-colors hover:text-accent',
          '[&[data-state=open]>svg]:rotate-180',
          className,
        )}
        {...props}
      >
        {children}
        {/* Rotation, not mirroring: a chevron pointing down means the same
            thing in both directions, so this icon must not flip under RTL. */}
        <ChevronDown
          className="size-4 shrink-0 text-text-muted transition-transform duration-[--hs-duration-base]"
          aria-hidden="true"
        />
      </RadixAccordion.Trigger>
    </RadixAccordion.Header>
  );
});

export const AccordionContent = forwardRef<
  ElementRef<typeof RadixAccordion.Content>,
  ComponentPropsWithoutRef<typeof RadixAccordion.Content>
>(function AccordionContent({ className, children, ...props }, ref) {
  return (
    <RadixAccordion.Content
      ref={ref}
      className={cn('overflow-hidden text-text-muted', className)}
      {...props}
    >
      <div className="pb-4">{children}</div>
    </RadixAccordion.Content>
  );
});
